// Conference Room Approvals panel — real sq_post_job_approvals rows tagged
// with this meeting's id (migration 20260715000001), reused verbatim from
// the Build Now Execute Layer's jobs.requestApproval/decideApproval. A
// meeting with no approval yet gets a minimal "shadow" sq_post_jobs row
// created server-side purely to satisfy that table's NOT NULL job_id FK —
// see api/square/command-center/jobs/request-approval.ts. Deciding requires
// the caller's real account role level (>= 80, canDecideApprovals), NOT
// Daily's isOwner/host flag — those are independent permission systems.

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, isAuthError } from '../../../../api/client'
import type { PostJobApproval } from '../../../../api/client'

const MONO = 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)'

const DECISION_COLOR: Record<PostJobApproval['decision'], string> = {
  pending: '#b8862a', approved: '#059669', rejected: '#dc2626', expired: '#5c6675',
}

function isMigrationError(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'backend_not_migrated'
}

export function ApprovalsPanel({ meetingId, canDecide }: { meetingId: string; canDecide?: boolean }) {
  const [approvals, setApprovals] = useState<PostJobApproval[]>([])
  const [loading, setLoading] = useState(true)
  const [authRequired, setAuthRequired] = useState(false)
  const [migrationRequired, setMigrationRequired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.commandCenter.jobs.approvalsForMeeting(meetingId)
      setApprovals(res.approvals)
      setAuthRequired(false)
      setMigrationRequired(false)
      setError(null)
    } catch (err) {
      if (isAuthError(err)) { setAuthRequired(true); setMigrationRequired(false); setError(null) }
      else if (isMigrationError(err)) { setMigrationRequired(true); setAuthRequired(false); setError(null) }
      else setError(err instanceof Error ? err.message : 'Could not load approvals.')
    } finally {
      setLoading(false)
    }
  }, [meetingId])

  useEffect(() => { void load() }, [load])

  const request = async () => {
    const approval_gate = draft.trim()
    if (!approval_gate || saving) return
    setSaving(true)
    try {
      await api.commandCenter.jobs.requestApproval({ meeting_id: meetingId, approval_gate })
      setDraft('')
      await load()
    } catch {
      // Honest no-op — the draft stays so the operator can retry.
    } finally {
      setSaving(false)
    }
  }

  const decide = async (approvalId: string, decision: 'approved' | 'rejected') => {
    setBusyId(approvalId)
    try {
      await api.commandCenter.jobs.decideApproval({ approval_id: approvalId, decision })
      await load()
    } catch {
      // Honest no-op.
    } finally {
      setBusyId(null)
    }
  }

  if (authRequired) return <p className="cr-state-note">Sign in to read this meeting's approvals.</p>
  if (migrationRequired) {
    return <p className="cr-state-note">This tab's storage column (sq_post_job_approvals.meeting_id) is source-ready but not yet applied in this environment — an operator must approve migration 20260715000001 before approvals persist here.</p>
  }
  if (error) return <p className="cr-state-note">{error}</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ fontFamily: MONO, fontSize: 10, color: '#8a93a3', marginBottom: 8 }}>LIVE · sq_post_job_approvals (meeting_id)</div>
      {!canDecide && (
        <p style={{ fontSize: 10.5, color: '#5c6675', fontFamily: MONO, lineHeight: 1.6, margin: '0 0 8px' }}>
          You can raise an approval here, but deciding one requires an operator account (role level 80+) — independent of who Daily marks as this call's host.
        </p>
      )}
      <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gap: 8, alignContent: 'start' }}>
        {loading && <p className="cr-state-note">Loading approvals…</p>}
        {!loading && approvals.length === 0 && <p className="cr-state-note">No approvals raised yet.</p>}
        {approvals.map((a) => (
          <div key={a.id} style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 12.5, color: '#e8ecf2' }}>{a.approval_gate}</span>
              <span style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 800, color: DECISION_COLOR[a.decision], textTransform: 'uppercase' }}>{a.decision}</span>
            </div>
            {a.required_by && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#8a93a3' }}>Required by: {a.required_by}</p>}
            {a.decision_note && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#8a93a3' }}>{a.decision_note}</p>}
            {canDecide && a.decision === 'pending' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button type="button" disabled={busyId === a.id} onClick={() => void decide(a.id, 'approved')}>Approve</button>
                <button type="button" disabled={busyId === a.id} onClick={() => void decide(a.id, 'rejected')}>Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void request() }}
          placeholder="Request an approval…"
          aria-label="Request an approval for this meeting"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: '#e8ecf2', fontSize: 12.5 }}
        />
        <button type="button" onClick={() => void request()} disabled={saving || !draft.trim()}>Request</button>
      </div>
    </div>
  )
}
